
import type { Party, PartyInvitation, PartyMember } from "../net/messages";
import { CHROME_ASSETS, elem, installChrome, styleInstaller } from "./chrome";

export interface PartyActions {
  /** Accept or decline, naming the token of the invitation on screen. */
  onAnswer?: (token: number, accept: boolean) => void;
  distanceTo?: (id: number) => number | null;
  onRequestAnswer?: (vid: number, accept: boolean) => void;
  /** Select one visible party member by the entity id in the roster. */
  onSelect?: (memberId: number) => void;
  /** Walk out. For a leader this dissolves the party, and the server says so. */
  onLeave?: () => void;
  /** Throw out one roster seat from the frame that drew it. */
  onKick?: (slot: number, rosterRevision: number) => void;
  /** Choose how the party divides what it earns: 0 by weight, 1 evenly. */
  onMode?: (mode: number) => void;
  /** Toggle attacker for one roster seat from the frame that drew it. */
  onAttacker?: (slot: number, assign: boolean, rosterRevision: number) => void;
  /** Toggle one remaining role in a roster seat from the frame that drew it. */
  onRole?: (
    slot: number,
    role: number,
    assign: boolean,
    rosterRevision: number,
  ) => void;
  /** Spend the leader's party heal. No arguments: the original's own request
   *  carries a skill index and a target vid of zero (`uiparty.py:524`). */
  onHeal?: () => void;
  /** Hand one held bag slot to a member: `SendExchangeStartPacket(vid)` then
   *  `SendExchangeItemAddPacket(slot, 0)` (`uiparty.py:347-352,369-373`). */
  onOfferTo?: (memberId: number, slot: number) => void;
}

/** `party.ExpMode`. Wire values, fixed by the original's own enum. */
export const EXP_BY_WEIGHT = 0;
export const EXP_EVENLY = 1;
export const PARTY_ROLE_ATTACKER = 2;
export const PARTY_ROLE_TANKER = 3;
export const PARTY_ROLE_BUFFER = 4;
export const PARTY_ROLE_SKILL_MASTER = 5;
export const PARTY_ROLE_HASTE = 6;
export const PARTY_ROLE_DEFENDER = 7;
// The shipped `PARTY_SET_*` nouns (`locale_game.txt:341-347`), not the field
// names: the original calls the tanker role "blade fighter", the buffer role
// "Blocker", and the haste role "Berserker".
const ROLE_NAMES: Record<number, string> = {
  [PARTY_ROLE_ATTACKER]: "Attacker", [PARTY_ROLE_TANKER]: "Blade fighter",
  [PARTY_ROLE_BUFFER]: "Blocker", [PARTY_ROLE_SKILL_MASTER]: "Skill Master",
  [PARTY_ROLE_HASTE]: "Berserker", [PARTY_ROLE_DEFENDER]: "Defender",
};

/** `__ShowStateButton`'s ladder: which roles a leader of this Tongsol level
 *  may hand out (`uiparty.py:196-224`), in the order it lays the buttons out.
 *  The server refuses the rest anyway (`party.py`'s `ROLE_LEADERSHIP`, from
 *  `party.cpp`); this is the board not offering what would be refused. */
export function roleChoicesFor(leadership: number): readonly number[] {
  const offered: number[] = [];
  if (leadership >= 10) offered.push(PARTY_ROLE_ATTACKER);
  if (leadership >= 20) offered.push(PARTY_ROLE_HASTE, PARTY_ROLE_TANKER);
  if (leadership >= 25) offered.push(PARTY_ROLE_BUFFER);
  if (leadership >= 35) offered.push(PARTY_ROLE_SKILL_MASTER);
  if (leadership >= 40) offered.push(PARTY_ROLE_DEFENDER);
  return offered;
}

/** `d:/ymir work/ui/game/windows/`, where `partymemberinfoboard.py` puts the
 *  member board's own art. */
const PARTY_ART = "/ui/textures/game/windows";

export const PARTY_WINDOW_X = 10; // uiparty.py:560
export const PARTY_WINDOW_Y = 52; // uiparty.py:560
export const PARTY_BOARD_WIDTH = 106; // uiparty.py:15, partymemberinfoboard.py:11
export const PARTY_BOARD_HEIGHT = 36; // partymemberinfoboard.py:12
const PARTY_BOARD_GAP = 2; // uiparty.py:704
const PARTY_MENU_BUTTON_ROOM = 20; // uiparty.py:701
const PARTY_MENU_GAP = 2; // uiparty.py:711
export const PARTY_MENU_WIDTH = 106; // uiparty.py:597
export const PARTY_MENU_HEIGHT = 70 + 70; // uiparty.py:597
const MENU_TOGGLE_WIDTH = 111; // party_menu_open_01.sub: right 502 - left 391
const MENU_TOGGLE_HEIGHT = 20; // party_menu_open_01.sub: bottom 40 - top 20
const MENU_BUTTON_SIZE = 32; // party_disband_01.sub: right 316 - left 284
const MENU_BUTTON_STEP = 37; // uiparty.py:478
const MENU_BUTTON_TOP = 15; // uiparty.py:483
const MODE_BUTTON_TOP = 85; // uiparty.py:487
const MODE_BUTTON_STEP = 25; // uiparty.py:490
const LARGE_BUTTON_WIDTH = 88; // large_button_01.sub, the radios' face (uiparty.py:427)
const STATE_BUTTON_WIDTH = 22; // party_state_normal_01.sub: right 266 - left 244
const STATE_BUTTON_HEIGHT = 36; // party_state_normal_01.sub: bottom 331 - top 295
const NAME_SLOT_X = 22; // partymemberinfoboard.py:34
const NAME_SLOT_WIDTH = 84; // partymemberinfoboard.py:36
const NAME_SLOT_HEIGHT = 17; // partymemberinfoboard.py:37
const GAUGE_Y = 17; // partymemberinfoboard.py:60
const AFFECT_Y = 24; // partymemberinfoboard.py:70
const AFFECT_SIZE = 12; // party_affect_experience.sub: right 256 - left 244
export const PARTY_STATE_MENU_X = 110; // uiparty.py:191
const PARTY_STATE_STEP = 23; // uiparty.py:198

/** The x of each of `partyAffectImageList`'s seven icons on the board, in that
 *  list's order (`partymemberinfoboard.py:69,78,87,96,105,114,123`). */
export const PARTY_AFFECT_X: readonly number[] = [22, 34, 46, 34, 46, 58, 70];

/** A member board's y in the window: `count * (board.GetHeight() + 2)`
 *  (`uiparty.py:704`). */
export function partyBoardTop(index: number): number {
  return index * (PARTY_BOARD_HEIGHT + PARTY_BOARD_GAP);
}

/** The window's height for this many boards: 20 for the menu button plus each
 *  board and its gap (`uiparty.py:700-708`). */
export function partyWindowHeight(count: number): number {
  return PARTY_MENU_BUTTON_ROOM + count * (PARTY_BOARD_HEIGHT + PARTY_BOARD_GAP);
}

/** The menu's screen y, 2 px under the window (`uiparty.py:710-711`). */
export function partyMenuTop(count: number): number {
  return PARTY_WINDOW_Y + partyWindowHeight(count) + PARTY_MENU_GAP;
}

/** `__ArrangeButtons`' x for each showing menu button: centred in the menu,
 *  starting at `(n - 1) * (-37 / 2)` under Python 2's floor division and
 *  stepping 37 (`uiparty.py:476-485`), placed at `parent / 2 - width / 2 + x`
 *  (`PythonWindow.cpp:286`). */
export function partyMenuButtonLefts(count: number): number[] {
  const lefts: number[] = [];
  let x = (count - 1) * Math.floor(-MENU_BUTTON_STEP / 2);
  for (let i = 0; i < count; i++) {
    lefts.push(Math.floor(PARTY_MENU_WIDTH / 2) - Math.floor(MENU_BUTTON_SIZE / 2) + x);
    x += MENU_BUTTON_STEP;
  }
  return lefts;
}

/** `MEMBER_BUTTON_IMAGE_FILE_NAME_DICT` by wire role (`uiparty.py:50-59`). */
const STATE_ART: Record<number, string> = {
  [PARTY_ROLE_ATTACKER]: "party_state_attacker",
  [PARTY_ROLE_TANKER]: "party_state_tanker",
  [PARTY_ROLE_BUFFER]: "party_state_buffer",
  [PARTY_ROLE_SKILL_MASTER]: "party_state_skill_master",
  [PARTY_ROLE_HASTE]: "party_state_berserker",
  [PARTY_ROLE_DEFENDER]: "party_state_defender",
};

/** Give a button the three faces `SetUp/Over/DownVisual` would, from one
 *  `game/windows` sprite name. */
function wearArt(button: HTMLElement, name: string): void {
  button.style.setProperty("--party-art-up", `url("${PARTY_ART}/${name}_01.png")`);
  button.style.setProperty("--party-art-over", `url("${PARTY_ART}/${name}_02.png")`);
  button.style.setProperty("--party-art-down", `url("${PARTY_ART}/${name}_03.png")`);
}

const STYLE_ID = "party-style";

const STYLE = `
/* PartyWindow: a bare window at (10, 52), as wide as one member board
   (uiparty.py:560, :708). Opacity below 1 keeps Chromium from culling the
   world canvas under it. */
.party {
  position: fixed;
  left: ${PARTY_WINDOW_X}px;
  top: ${PARTY_WINDOW_Y}px;
  width: ${PARTY_BOARD_WIDTH}px;
  z-index: 58;
  pointer-events: auto;
  font: 12px/1 Tahoma, "MS Sans Serif", sans-serif;
  color: #c2c2c2;
  opacity: 0.999;
}
.party-art {
  padding: 0;
  border: 0;
  font-size: 0;
  color: transparent;
  cursor: pointer;
  background: var(--party-art-up) no-repeat left top;
  image-rendering: pixelated;
}
.party-art:hover:not(:disabled) { background-image: var(--party-art-over); }
.party-art:active:not(:disabled) { background-image: var(--party-art-down); }
/* PartyMemberInfoBoard, 106x36 (partymemberinfoboard.py:11-12). */
.party-member {
  position: absolute;
  left: 0;
  width: ${PARTY_BOARD_WIDTH}px;
  height: ${PARTY_BOARD_HEIGHT}px;
}
/* StateButton at (0, 0) (partymemberinfoboard.py:18-26). */
.party-member-state-button {
  position: absolute;
  left: 0;
  top: 0;
  width: ${STATE_BUTTON_WIDTH}px;
  height: ${STATE_BUTTON_HEIGHT}px;
}
/* NameSlot: an 84x17 bar of 0x99000000 at (22, 0) (partymemberinfoboard.py:29-39). */
.party-member-name-slot {
  position: absolute;
  left: ${NAME_SLOT_X}px;
  top: 0;
  width: ${NAME_SLOT_WIDTH}px;
  height: ${NAME_SLOT_HEIGHT}px;
  background: rgba(0, 0, 0, 0.6);
}
/* NamePrint at (3, 2) in LINK_COLOR, UNLINK_COLOR once unlinked
   (partymemberinfoboard.py:46-47, uiparty.py:19-20, :303-310). */
.party-member-select {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  padding: 2px 0 0 3px;
  border: 0;
  background: transparent;
  color: rgb(194, 194, 194);
  font: inherit;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  cursor: pointer;
}
.party-member-away .party-member-select { color: rgb(127, 127, 127); }
/* Gauge, 84 wide and red, at (22, 17) (partymemberinfoboard.py:54-63). */
.party-member-hp {
  position: absolute;
  left: ${NAME_SLOT_X}px;
  top: ${GAUGE_Y}px;
  width: ${NAME_SLOT_WIDTH}px;
}
.party-member-hp .m2-gauge-fill::after { content: none; }
.party-member-hp-fill {
  /* A box, so SetPercentage's width lands (uiparty.py:273). */
  display: block;
  height: 100%;
  background: url("${CHROME_ASSETS.gaugeRed}") left top repeat-x;
  image-rendering: pixelated;
}
.party-member-hp-unknown .party-member-hp-fill { display: none; }
/* Unlink hides the gauge and every affect (uiparty.py:307-310). */
.party-member-away .party-member-hp,
.party-member-away .party-member-affect { visibility: hidden; }
.party-member-affect {
  position: absolute;
  top: ${AFFECT_Y}px;
  width: ${AFFECT_SIZE}px;
  height: ${AFFECT_SIZE}px;
  font-size: 0;
  color: transparent;
  background: no-repeat left top;
}
/* __ShowStateButton: a row of 22x36 buttons from x + 110, 23 apart
   (uiparty.py:188-239). */
.party-member-states {
  position: absolute;
  left: ${PARTY_STATE_MENU_X}px;
  top: 0;
  display: flex;
  gap: ${PARTY_STATE_STEP - STATE_BUTTON_WIDTH}px;
  z-index: 1;
}
.party-member-states[hidden] { display: none; }
.party-member-states > .party-art {
  width: ${STATE_BUTTON_WIDTH}px;
  height: ${STATE_BUTTON_HEIGHT}px;
  flex: none;
}
.party-member-focus {
  position: absolute;
  left: ${PARTY_STATE_MENU_X}px;
  top: 2px;
  color: #ffcf8f;
  font-size: 11px;
  white-space: nowrap;
}
/* The menu button: centred and bottom-aligned at 20 (uiparty.py:585-588),
   so parent / 2 - 111 / 2 across and parent height - 20 down
   (PythonWindow.cpp:233-234, :286). */
.party-menu-button {
  position: absolute;
  left: ${Math.floor(PARTY_BOARD_WIDTH / 2) - Math.floor(MENU_TOGGLE_WIDTH / 2)}px;
  bottom: 0;
  width: ${MENU_TOGGLE_WIDTH}px;
  height: ${MENU_TOGGLE_HEIGHT}px;
}
/* PartyMenu: a 106x140 ThinBoard at (10, window bottom + 2) (uiparty.py:596-599, :710-711). */
.party-menu {
  position: fixed;
  left: ${PARTY_WINDOW_X}px;
  width: ${PARTY_MENU_WIDTH}px;
  height: ${PARTY_MENU_HEIGHT}px;
}
.party-menu[hidden] { display: none; }
.party-menu > .party-button {
  position: absolute;
  top: ${MENU_BUTTON_TOP}px;
  width: ${MENU_BUTTON_SIZE}px;
  height: ${MENU_BUTTON_SIZE}px;
}
/* MakeTextLine centres the title in the menu both ways (ui.py:3580-3588). */
.party-mode-title {
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  text-align: center;
  color: #c2c2c2;
  pointer-events: none;
}
/* The two radios: large buttons centred at y = 85 and 110 (uiparty.py:420-445, :487-490). */
.party-mode {
  position: absolute;
  left: ${Math.floor(PARTY_MENU_WIDTH / 2) - Math.floor(LARGE_BUTTON_WIDTH / 2)}px;
  color: #e8dcc0;
  font: 12px/21px Tahoma, "MS Sans Serif", sans-serif;
}
.party-mode-level { top: ${MODE_BUTTON_TOP}px; }
.party-mode-parity { top: ${MODE_BUTTON_TOP + MODE_BUTTON_STEP}px; }
.party-mode[aria-checked="true"] { background-image: url("${CHROME_ASSETS.largeDownState}"); }
.party-mode:disabled { cursor: default; }

.party-invite {
  position: fixed;
  left: 50%;
  top: 28%;
  transform: translateX(-50%);
  z-index: 63;
  padding: 12px 16px;
  border-radius: 10px;
  background: rgba(12, 14, 18, 0.95);
  border: 1px solid rgba(170, 200, 255, 0.28);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
  pointer-events: auto;
  font: 13px/1.5 system-ui, sans-serif;
  color: #d8dee6;
  text-align: center;
}
.party-invite-text { margin-bottom: 8px; }
.party-invite-buttons { display: flex; gap: 8px; }
`;

const installStyle = styleInstaller(STYLE_ID, STYLE, installChrome);

/** Whether this client's own entity leads the party on screen. */
export function isLeader(party: Party, self: number): boolean {
  return party.leader_id !== 0 && party.leader_id === self;
}

/** The icon `partyAffectImageList[kind]` shows, in that list's order
 *  (`uiparty.py:104-112`). The order is not the names': index 2 is the
 *  DefenderImage and index 6 the RegenBonus, so the tanker's icon is the
 *  defence grade and the defender's is the regen bonus, exactly as the pack
 *  wires them (`partymemberinfoboard.py:72-126`). Seven, not the nine the
 *  board builds: the packet carries `sAffects[0..6]`
 *  (`PythonPlayerModule.cpp:1846-1852`), so the last two images are never
 *  shown and this build does not ship them. */
export const PARTY_AFFECT_ICONS: readonly string[] = [
  "party_affect_experience",
  "party_affect_attackgrade",
  "party_affect_defencegrade",
  "party_affect_attackgrade",
  "party_affect_attackgrade",
  "party_affect_timebonus",
  "party_affect_regenbonus",
];

const PARTY_AFFECT_NAMES: Record<number, string> = {
  0: "Exp",
  1: "Atk",
  2: "Tank",
  3: "Buffer",
  4: "Skill",
  5: "Haste",
  6: "Def",
};

/** Draw one server-supplied legacy party affect without deriving its value. */
export function formatPartyAffect(kind: number, value: number): string {
  const name = PARTY_AFFECT_NAMES[kind];
  return name === undefined || value <= 0 ? "" : `${name} +${value}`;
}

export function formatInvitationExpiry(seconds: number): string {
  const remaining = Math.max(0, Math.trunc(seconds));
  return ` · ${remaining}s left`;
}

/** PARTY_DO_YOU_JOIN (`locale_game.txt:326`), a suffix like its sibling
 *  PARTY_DO_YOU_ACCEPT: `RecvPartyInviteQuestion` sets the dialog to
 *  `leaderName + localeInfo.PARTY_DO_YOU_JOIN` and nothing more
 *  (`game.py:954-961`). Leading space and all. */
export const PARTY_DO_YOU_JOIN = " has invited you to join his group. Accept?";

export const PARTY_QUESTION_YES = "Yes";
export const PARTY_QUESTION_NO = "No";

export const PARTY_INVITE_ANSWER_RANGE_MM = 50_000;

/** The answer that actually goes on the wire, given what the player pressed
 *  and how far away the inviter is. `null` distance is the original's
 *  `distance < 0.0`: no such vid. */
export function partyInviteAnswerInRange(
  clicked: boolean,
  distanceMm: number | null,
): boolean {
  if (!clicked) return false;
  if (distanceMm === null || distanceMm < 0 || distanceMm > PARTY_INVITE_ANSWER_RANGE_MM) {
    return false;
  }
  return true;
}

export function parsePartyRequest(text: string): number | null {
  const match = /^PartyRequest (\d+)$/.exec(text);
  if (match === null) return null;
  const vid = Number(match[1]);
  return Number.isSafeInteger(vid) && vid > 0 ? vid : null;
}

/** PARTY_DO_YOU_ACCEPT (`locale_game.txt:325`). A suffix in the original --
 *  the asker's name is concatenated in front of it, leading space and all. */
export const PARTY_DO_YOU_ACCEPT = " would like to join the Group.";

/** PARTY_REQUEST_DENIED (`locale_game.txt:340`) -- what the asker is told on a
 *  `PartyRequestDenied` line, which the server sends on a deny, on a second
 *  request while one stands, and when the ten seconds lapse
 *  (`char.cpp:4144-4163,4238`). */
export const PARTY_REQUEST_DENIED = "Your request has been declined.";

export class PartyPanel {
  private readonly root: HTMLElement;
  private readonly list: HTMLElement;
  private readonly menuButton: HTMLButtonElement;
  private readonly menu: HTMLElement;
  private readonly levelMode: HTMLButtonElement;
  private readonly parityMode: HTMLButtonElement;
  private readonly heal: HTMLButtonElement;
  private readonly leave: HTMLButtonElement;

  private readonly prompt: HTMLElement;
  private readonly promptText: HTMLElement;
  private readonly accept: HTMLButtonElement;
  private readonly decline: HTMLButtonElement;

  /** This client's own entity id, so the panel knows which row is theirs. Zero
   *  until the connection has one, which is before any party can exist. */
  private self = 0;
  /** See `armBagSlot`. */
  private armed: number | null = null;

  private drawn: PartyInvitation | null = null;

  private answered = -1;

  /** The vid of the join request on screen, when what is drawn is a request
   *  rather than an invitation. Null otherwise, and never both: one prompt,
   *  one question, so the answer cannot be sent down the wrong channel. */
  private requester: number | null = null;

  private party: Party | null = null;

  private readonly focus = new Map<number, string>();

  /** The member whose state buttons are out, or null (`isShowStateButton`,
   *  `uiparty.py:188-244`). */
  private statesFor: number | null = null;

  constructor(
    container: HTMLElement,
    private readonly actions: PartyActions = {},
  ) {
    const doc = container.ownerDocument;
    installStyle(doc);

    this.root = elem(doc, "div", "party");
    this.root.hidden = true;

    this.list = elem(doc, "div", "party-list");

    this.menuButton = doc.createElement("button");
    this.menuButton.type = "button";
    this.menuButton.className = "party-menu-button party-art";
    wearArt(this.menuButton, "party_menu_open");
    this.menuButton.addEventListener("click", () => this.toggleMenu());

    this.menu = elem(doc, "div", "party-menu m2-thinboard");
    this.menu.hidden = true;

    // PARTY_EXP_DISTRIBUTION_MODE/_LEVEL/_PARITY (`locale_game.txt:327,328,330`
    // -- "EXP Distribution", "Level dependant", "even"), the shipped words for
    // this exact radio group (`uiparty.py:419,430,442`), typo and casing kept.
    const modeTitle = elem(doc, "div", "party-mode-title", "EXP Distribution");
    this.levelMode = doc.createElement("button");
    this.levelMode.type = "button";
    this.levelMode.className = "party-mode party-mode-level m2-large";
    this.levelMode.setAttribute("role", "radio");
    this.levelMode.textContent = "Level dependant";
    this.levelMode.title = "Weight experience by level";
    this.levelMode.addEventListener("click", () => this.chooseMode(EXP_BY_WEIGHT));
    this.parityMode = doc.createElement("button");
    this.parityMode.type = "button";
    this.parityMode.className = "party-mode party-mode-parity m2-large";
    this.parityMode.setAttribute("role", "radio");
    this.parityMode.textContent = "even";
    this.parityMode.title = "Share experience evenly";
    this.parityMode.addEventListener("click", () => this.chooseMode(EXP_EVENLY));

    // PARTY_HEAL_ALL_MEMBER is "Restore everything." (`locale_game.txt:332`),
    // on the Party_Skill_Heal face (`uiparty.py:458-461`).
    this.heal = doc.createElement("button");
    this.heal.type = "button";
    this.heal.className = "party-button party-heal party-art";
    this.heal.textContent = "Restore everything";
    this.heal.title = "Restore everything.";
    this.heal.hidden = true;
    wearArt(this.heal, "party_skill_heal");
    this.heal.addEventListener("click", () => {
      // `OnPartyUseSkill` hides the button on the click, before any answer
      // comes back (`uiparty.py:523-525`). The `Party` frame the server sends
      // straight after is what actually decides whether it comes back.
      this.heal.hidden = true;
      this.arrangeMenuButtons();
      this.actions.onHeal?.();
    });
    this.leave = doc.createElement("button");
    this.leave.type = "button";
    this.leave.className = "party-button party-leave party-art";
    this.leave.textContent = "Leave group";
    this.leave.addEventListener("click", () => this.actions.onLeave?.());

    this.menu.append(this.leave, this.heal, modeTitle, this.levelMode, this.parityMode);
    this.root.append(this.list, this.menuButton, this.menu);

    this.prompt = elem(doc, "div", "party-invite");
    this.prompt.hidden = true;
    this.promptText = elem(doc, "div", "party-invite-text");
    const promptButtons = elem(doc, "div", "party-invite-buttons");
    this.accept = doc.createElement("button");
    this.accept.type = "button";
    this.accept.className = "party-button party-invite-accept m2-middle";
    this.accept.textContent = PARTY_QUESTION_YES;
    this.accept.addEventListener("click", () => this.answer(true));
    this.decline = doc.createElement("button");
    this.decline.type = "button";
    this.decline.className = "party-button party-invite-decline m2-middle";
    this.decline.textContent = PARTY_QUESTION_NO;
    this.decline.addEventListener("click", () => this.answer(false));
    promptButtons.append(this.accept, this.decline);
    this.prompt.append(this.promptText, promptButtons);

    container.append(this.root, this.prompt);
  }

  get showing(): boolean {
    return !this.root.hidden;
  }

  get asking(): boolean {
    return !this.prompt.hidden;
  }

  /** The token Accept would send. -1 when no prompt is drawn. For tests. */
  get shownToken(): number {
    return this.drawn === null ? -1 : this.drawn.token;
  }

  /** The bag cell the inventory says is held, told rather than discovered --
   *  `mouseModule.mouseController.isAttached()` is what the member board reads
   *  (`uiparty.py:347-348`). Null between gestures. */
  armBagSlot(slot: number | null): void {
    this.armed = slot;
  }

  /** `OnMouseLeftButtonDown` (`uiparty.py:341-361`): target the member; while
   *  something is held, start a trade with them and put the held cell on it;
   *  otherwise a leader toggles that seat's state buttons. */
  private clickMember(member: PartyMember): void {
    this.actions.onSelect?.(member.entity_id);
    const armed = this.armed;
    if (armed !== null) {
      this.armed = null;
      this.actions.onOfferTo?.(member.entity_id, armed);
      return;
    }
    const party = this.party;
    if (party === null || !isLeader(party, this.self) || member.entity_id === this.self) return;
    this.statesFor = this.statesFor === member.entity_id ? null : member.entity_id;
    this.update(party);
  }

  /** `OnTogglePartyMenu` (`uiparty.py:733-743`). */
  private toggleMenu(): void {
    this.menu.hidden = !this.menu.hidden;
    wearArt(this.menuButton, this.menu.hidden ? "party_menu_open" : "party_menu_close");
  }

  /** `__ArrangeButtons` over the buttons `__ShowButton` has appended: the
   *  leave or break-up button, then the heal (`uiparty.py:476-528`). */
  private arrangeMenuButtons(): void {
    const showing = [this.leave, this.heal].filter((button) => !button.hidden);
    const lefts = partyMenuButtonLefts(showing.length);
    showing.forEach((button, index) => {
      button.style.left = `${lefts[index]}px`;
    });
  }

  /** Whose client this is. The roster needs it to know which row not to offer a
   *  kick button for. */
  setSelf(entityId: number): void {
    this.self = entityId;
    if (this.party !== null) this.update(this.party);
  }

  focused(who: number, targetId: number, name: string): void {
    const before = this.focus.get(who);
    if (targetId === 0 || name === "") {
      this.focus.delete(who);
    } else {
      this.focus.set(who, name);
    }
    if (this.focus.get(who) === before) return;
    if (this.party !== null) this.update(this.party);
  }

  update(party: Party): void {
    this.party = party;
    if (party.leader_id === 0) {
      // `ExitParty` hides the menu with the window (`uiparty.py:693-696`).
      this.root.hidden = true;
      this.menu.hidden = true;
      wearArt(this.menuButton, "party_menu_open");
      this.statesFor = null;
      this.list.replaceChildren();
      return;
    }

    const doc = this.root.ownerDocument;
    const leading = isLeader(party, this.self);
    if (!leading) this.statesFor = null;
    this.root.style.height = `${partyWindowHeight(party.members.length)}px`;
    this.menu.style.top = `${partyMenuTop(party.members.length)}px`;

    this.list.replaceChildren(
      ...party.members.map((member: PartyMember, slot: number) => {
        const row = elem(doc, "div", "party-member");
        row.style.top = `${partyBoardTop(slot)}px`;
        // Unlinked rather than removed: a member out of range keeps the board
        // (`uiparty.py:307-310`).
        if (!member.near) row.classList.add("party-member-away");

        // `StateButton` at (0,0), wearing the seat's state (`uiparty.py:275-289`).
        const state = doc.createElement("button");
        state.type = "button";
        state.className = "party-member-state-button party-art";
        wearArt(state, member.leader ? "party_state_leader" : STATE_ART[member.role] ?? "party_state_normal");
        if (member.role in ROLE_NAMES) {
          state.dataset.state = String(member.role);
          state.textContent = member.role_bonus > 0
            ? `${ROLE_NAMES[member.role]} +${member.role_bonus}`
            : `${ROLE_NAMES[member.role]} (away)`;
        }
        state.title = `State: ${ROLE_NAMES[member.role] ?? "none"}`;
        state.addEventListener("click", () => this.actions.onSelect?.(member.entity_id));

        const nameSlot = elem(doc, "div", "party-member-name-slot");
        const name = doc.createElement("button");
        name.type = "button";
        name.className = "party-member-name party-member-select";
        name.textContent = member.name;
        name.title = `Target ${member.name}`;
        name.addEventListener("click", () => this.clickMember(member));
        name.addEventListener("contextmenu", (event) => {
          // `OnMouseRightButtonDown` is the left click (`uiparty.py:375-376`).
          event.preventDefault();
          this.clickMember(member);
        });
        nameSlot.append(name);
        row.append(state, nameSlot);

        // Party HP is a server-owned percentage, matching the original
        // `GetPartyMemberHPPercentage` read (`uiparty.py:654`).  255 is the
        // legacy offline sentinel (`party.cpp:795-806`), not a percentage and
        // must never become a full bar through a client-side clamp.
        const hp = elem(doc, "div", "party-member-hp m2-gauge");
        const track = elem(doc, "div", "m2-gauge-fill");
        hp.append(track);
        hp.setAttribute("aria-label", member.hp_percent === 255
          ? "HP unavailable"
          : `HP ${Math.max(0, Math.min(100, member.hp_percent))}%`);
        if (member.hp_percent === 255) {
          hp.classList.add("party-member-hp-unknown");
          hp.title = "HP unavailable";
        } else {
          const fill = elem(doc, "span", "party-member-hp-fill");
          const percent = Math.max(0, Math.min(100, member.hp_percent));
          fill.style.width = `${percent}%`;
          hp.title = `HP ${percent}%`;
          track.append(fill);
        }
        row.append(hp);

        for (const affect of member.affects) {
          const label = formatPartyAffect(affect.kind, affect.value);
          const icon = PARTY_AFFECT_ICONS[affect.kind];
          if (label === "" || icon === undefined) continue;
          const badge = elem(doc, "span", "party-member-affect", label);
          badge.title = label;
          badge.style.left = `${PARTY_AFFECT_X[affect.kind]}px`;
          badge.style.backgroundImage = `url("${PARTY_ART}/${icon}.png")`;
          row.append(badge);
        }

        const quarry = this.focus.get(member.entity_id);
        if (quarry !== undefined) {
          const focus = elem(doc, "span", "party-member-focus", `▸ ${quarry}`);
          focus.title = `Fighting ${quarry}`;
          row.append(focus);
        }

        if (leading && member.entity_id !== this.self) {
          const states = elem(doc, "div", "party-member-states");
          states.hidden = this.statesFor !== member.entity_id;
          // `__AppendStateButton` draws the NORMAL (take-it-off) button in
          // place of a seat's own role and the ladder's buttons otherwise
          // (`uiparty.py:179-186`).
          const choices = member.role in ROLE_NAMES
            ? [member.role]
            : roleChoicesFor(party.leadership);
          for (const choice of choices) {
            const role = doc.createElement("button");
            role.type = "button";
            role.className = "party-role party-art";
            const assigned = member.role === choice;
            wearArt(role, assigned ? "party_state_normal" : STATE_ART[choice]);
            role.title = assigned ? `Remove ${ROLE_NAMES[choice]} from ${member.name}` : `Make ${member.name} the ${ROLE_NAMES[choice]}`;
            role.textContent = assigned ? `−${ROLE_NAMES[choice]}` : `+${ROLE_NAMES[choice]}`;
            role.addEventListener("click", () => {
              this.statesFor = null; // uiparty.py:315
              if (choice === PARTY_ROLE_ATTACKER) {
                this.actions.onAttacker?.(slot, !assigned, party.roster_revision);
              } else {
                this.actions.onRole?.(slot, choice, !assigned, party.roster_revision);
              }
            });
            states.append(role);
          }
          // MEMBER_BUTTON_WARP is built but never shown: its branch in
          // `__ShowStateButton` is commented out (`uiparty.py:226-231`).
          // MEMBER_BUTTON_EXPEL last (`uiparty.py:233-239`), TARGET_BUTTON_EXCLUDE
          // (`locale_game.txt:539`).
          const kick = doc.createElement("button");
          kick.type = "button";
          kick.className = "party-kick party-art";
          wearArt(kick, "party_expel");
          kick.title = `Remove ${member.name}`;
          kick.textContent = "×";
          kick.addEventListener("click", () => {
            this.statesFor = null; // uiparty.py:335
            this.actions.onKick?.(slot, party.roster_revision);
          });
          states.append(kick);
          row.append(states);
        }
        return row;
      }),
    );

    // Greyed, not hidden, and not enforced: the server refuses a mode change
    // from anybody but the leader either way. The radio state is strictly the
    // frame the server last sent, never an optimistic result of the click.
    this.levelMode.disabled = !leading;
    this.parityMode.disabled = !leading;
    this.levelMode.setAttribute("aria-checked", String(party.mode === EXP_BY_WEIGHT));
    this.parityMode.setAttribute("aria-checked", String(party.mode === EXP_EVENLY));
    // PARTY_BREAK_UP on Party_Disband for the leader, PARTY_LEAVE on Party_Exit
    // otherwise (`uiparty.py:462-471`, `:513-521`, `locale_game.txt:324,335`);
    // both send the one exit request.
    this.leave.textContent = leading ? "Delete Group" : "Leave group";
    this.leave.title = this.leave.textContent;
    wearArt(this.leave, leading ? "party_disband" : "party_exit");
    // Shown only for the leader: the original creates the heal button hidden
    // and only ever `__ShowButton`s it from the server's readiness signal,
    // which only a leader receives (`uiparty.py:513-528`, `party.cpp:1413-1418`).
    this.heal.hidden = !(leading && party.heal_ready);
    this.arrangeMenuButtons();
    this.root.hidden = false;
  }

  invited(invitation: PartyInvitation): void {
    if (invitation.token === 0) {
      this.drawn = null;
      // A request on screen is not this invitation's to take down.
      if (this.requester === null) this.prompt.hidden = true;
      return;
    }
    this.drawn = invitation;
    this.requester = null;
    this.accept.textContent = PARTY_QUESTION_YES;
    this.decline.textContent = PARTY_QUESTION_NO;
    const who = invitation.inviter_name || "Somebody";
    // `leaderName + PARTY_DO_YOU_JOIN`, exactly (`game.py:954-961`). No timer:
    // the original's question dialog carries no countdown, and the seconds on
    // this frame are the server's own expiry, which it enforces by taking the
    // invitation down rather than by printing it at the player.
    this.promptText.textContent = `${who}${PARTY_DO_YOU_JOIN}`;
    this.setAnswerable(true);
    this.prompt.hidden = false;
  }

  requested(vid: number, name: string): void {
    if (vid <= 0) {
      this.requester = null;
      this.prompt.hidden = true;
      return;
    }
    this.drawn = null;
    this.requester = vid;
    this.promptText.textContent = `${name || "Somebody"}${PARTY_DO_YOU_ACCEPT}`;
    this.accept.textContent = PARTY_QUESTION_YES;
    this.decline.textContent = PARTY_QUESTION_NO;
    this.setAnswerable(true);
    this.prompt.hidden = false;
  }

  private answer(accept: boolean): void {
    const asker = this.requester;
    if (asker !== null) {
      // One answer per request: the server keys the pending event on the
      // asker alone, so a second click is a second command it would refuse.
      this.requester = null;
      this.setAnswerable(false);
      this.prompt.hidden = true;
      this.actions.onRequestAnswer?.(asker, accept);
      return;
    }
    const drawn = this.drawn;
    // The token of what is on screen. Never `this.connection.invitation`, and
    // never a re-read: if a second invitation has arrived since this one was
    // drawn, the server will refuse this token, and refusing is the correct
    // outcome -- the player agreed to what they read.
    if (drawn === null || drawn.token === this.answered) return;
    this.answered = drawn.token;
    this.setAnswerable(false);
    // The original overwrites a Yes pressed too far from the inviter, and says
    // nothing about it (`game.py:963-974`). See `partyInviteAnswerInRange`.
    const sent =
      this.actions.distanceTo === undefined
        ? accept
        : partyInviteAnswerInRange(accept, this.actions.distanceTo(drawn.inviter_id));
    this.actions.onAnswer?.(drawn.token, sent);
  }

  private setAnswerable(can: boolean): void {
    this.accept.disabled = !can;
    this.decline.disabled = !can;
  }

  private chooseMode(mode: number): void {
    const party = this.party;
    if (party === null || !isLeader(party, this.self)) return;
    this.actions.onMode?.(mode);
  }
}
